import Vue from 'vue'
import { computed } from '@vue/composition-api'

// Shared state for the primary navigation drawer (LIQ-2643, LIQ-2765).
//
// Desktop (>= OVERLAY_BREAKPOINT): the drawer is a persistent panel beside the
// page, like the Q Portal - the hamburger collapses it to an icon-only rail and
// expands it again, and the page content resizes to match. The choice is kept
// in localStorage so it survives navigation and reloads. Below the breakpoint
// it is an overlay that starts closed.
//
// Vue.observable (not ref) because this module is evaluated before
// VueCompositionAPI is installed.

// The docked panel's widths - match $drawer-width / $rail-width in AppNavDrawer.vue.
export const NAV_DRAWER_WIDTH = 272
export const NAV_RAIL_WIDTH = 64
export const OVERLAY_BREAKPOINT = 992

const STORAGE_KEY = 'limeiq_nav_drawer_expanded'

const state = Vue.observable({
  expanded: true,
  overlayMode: false,
  mounted: 0,
  top: 0,
  // Menu flags only the header knows (new data, uploads, report on screen, ...) - set by
  // AppNavbar, read by useNavMenu for the menu rail and the home page tiles.
  hostContext: {}
})

let initialised = false

const isOverlayViewport = () => window.innerWidth < OVERLAY_BREAKPOINT

// The desktop expanded/collapsed choice; storage can be blocked (private mode),
// in which case the panel simply starts expanded.
const readDockedExpanded = () => {
  try {
    return '0' !== window.localStorage.getItem(STORAGE_KEY)
  } catch (e) {
    return true
  }
}

const setExpanded = (value) => {
  state.expanded = value
  // The overlay always starts closed, so only the docked state is remembered.
  if (state.overlayMode) return
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
  } catch (e) {
    // Not persisted - the choice still holds for this page load.
  }
}

// Distance from the viewport top to the bottom of the header, so the panel
// sits under it and follows it as the page scrolls.
const updateTop = () => {
  const header = document.getElementById('main-navbar')
  state.top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0
}

const onResize = () => {
  const overlay = isOverlayViewport()

  if (overlay !== state.overlayMode) {
    state.overlayMode = overlay
    // Crossing the breakpoint resets to that mode's default.
    state.expanded = overlay ? false : readDockedExpanded()
  }

  updateTop()
}

const init = () => {
  if (initialised) return
  initialised = true

  state.overlayMode = isOverlayViewport()
  state.expanded = state.overlayMode ? false : readDockedExpanded()

  window.addEventListener('resize', onResize)
  window.addEventListener('scroll', updateTop, { passive: true })
}

export const useNavDrawer = () => {
  const isPinned = computed(() => state.expanded && !state.overlayMode)
  // Collapsed on desktop: the icon-only rail stays docked beside the page.
  const isRail = computed(() => !state.expanded && !state.overlayMode)

  // Left margin the page needs for the docked panel or rail.
  const contentOffset = computed(() => {
    if (!state.mounted || state.overlayMode) return '0px'
    return `${isPinned.value ? NAV_DRAWER_WIDTH : NAV_RAIL_WIDTH}px`
  })

  return {
    state,
    isPinned,
    isRail,
    contentOffset,

    register() {
      init()
      state.mounted += 1
      updateTop()
    },
    unregister() {
      state.mounted = Math.max(0, state.mounted - 1)
    },
    updateTop,
    open: () => setExpanded(true),
    close: () => setExpanded(false),
    toggle: () => setExpanded(!state.expanded)
  }
}
